import { Injectable } from '@angular/core';
import { Point } from '../../../model/geometry/point';
import { ClimateService } from '../../../data-access-layer/climate.service';
import { ClimateChart} from '../../../model/climate/climate-chart';
import { BioclimaticRegion } from '../../../model/climate/bioclimatic-region';
import { MeanTemperatureService } from './mean-temperature.service';
import { MinTemperatureService } from './min-temperature.service';
import { PopulationDensityService } from './population-density.service';
import { PrecipitationService } from './precipitation.service';
import { SeaLevelRiseService } from './sea-level-rise.service';
import { SnowfallService } from './snowfall.service';
import { SurfaceWindService } from './surface-wind.service';
import { MaxTemperatureService } from './max-temperature.service';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { take, tap, catchError } from 'rxjs/operators';
import { SeaLevelRiseWarmingService } from './sea-level-rise-warming.service';
import { Spi6Service } from './spi6.service';
import { Spi6ScoreService } from './spi6-score.service';
import { Spi6ChangeService } from './spi6-change.service';
import { CoolingDegreeDaysService } from './cooling-degree-days.service';
import { CoolingDegreeDaysScoreService } from './cooling-degree-days-score.service';
import { CoolingDegreeDaysChangeService } from './cooling-degree-days-change.service';
import { TxxService } from './txx.service';
import { TxxScoreService } from './txx-score.service';
import { TxxChangeService } from './txx-change.service';
import { ClimateTService } from './climate-t.service';
import { ClimateTScoreService } from './climate-t-score.service';
import { ClimateTChangeService } from './climate-t-change.service';
import { FrostDaysService } from './frost-days.service';
import { FrostDaysScoreService } from './frost-days-score.service';
import { FrostDaysChangeService } from './frost-days-change.service';
import { HeatingDegreeDaysService } from './heating-degree-days.service';
import { HeatingDegreeDaysScoreService } from './heating-degree-days-score.service';
import { HeatingDegreeDaysChangeService } from './heating-degree-days-change.service';
import { ExtremeHotDaysService } from './extreme-hot-days.service';
import { ExtremeHotDaysScoreService } from './extreme-hot-days-score.service';
import { ExtremeHotDaysChangeService } from './extreme-hot-days-change.service';
import { VeryExtremeHotDaysService } from './very-extreme-hot-days.service';
import { VeryExtremeHotDaysScoreService } from './very-extreme-hot-days-score.service';
import { VeryExtremeHotDaysChangeService } from './very-extreme-hot-days-change.service';
import { ClimatePrService } from './climate-pr.service';
import { ClimatePrScoreService } from './climate-pr-score.service';
import { ClimatePrChangeService } from './climate-pr-change.service';
import { ClimateCddService } from './climate-cdd.service';
import { ClimateCddScoreService } from './climate-cdd-score.service';
import { ClimateCddChangeService } from './climate-cdd-change.service';
import { ClimateRx1dayService } from './climate-rx1day.service';
import { ClimateRx1dayScoreService } from './climate-rx1day-score.service';
import { ClimateRx1dayChangeService } from './climate-rx1day-change.service';
import { ClimateRx5dayService } from './climate-rx5day.service';
import { ClimateRx5dayScoreService } from './climate-rx5day-score.service';
import { ClimateRx5dayChangeService } from './climate-rx5day-change.service';
import { ClimatePrsnService } from './climate-prsn.service';
import { ClimatePrsnScoreService } from './climate-prsn-score.service';
import { ClimatePrsnChangeService } from './climate-prsn-change.service';
import { ClimateSstService } from './climate-sst.service';
import { ClimateSstScoreService } from './climate-sst-score.service';
import { ClimateSstChangeService } from './climate-sst-change.service';
import { ClimateSfcWindService } from './climate-sfcwind.service';
import { ClimateSfcWindScoreService } from './climate-sfcwind-score.service';
import { ClimateSfcWindChangeService } from './climate-sfcwind-change.service';
import { ClimateTrService } from './climate-tr.service';
import { ClimateTrScoreService } from './climate-tr-score.service';
import { ClimateTrChangeService } from './climate-tr-change.service';
import { ClimateSlrService } from './climate-slr.service';
import { ClimateSlrScoreService } from './climate-slr-score.service';
import { ClimateSlrChangeService } from './climate-slr-change.service';
import { MAPTYCS_CHART_COLOR } from './custom-theme';

@Injectable()
export class ClimateOverlayPermission {
  isMaxTemperatureEnabled: boolean;
  isMeanTemperatureEnabled: boolean;
  isMinTemperatureEnabled: boolean;
  isSnowFallEnabled: boolean;
  isPrecipitationEnabled: boolean;
  isSurfaceWindEnabled: boolean;
  isPopulationDensityEnabled: boolean;
  isSeaLevelRiseEnabled: boolean;
  isSeaLevelRiseWarmingEnabled: boolean;
  isSpi6Enabled: boolean;
  isSpi6ScoreEnabled: boolean;
  isSpi6ChangeEnabled: boolean;
  isCoolingDegreeDaysEnabled: boolean;
  isCoolingDegreeDaysScoreEnabled: boolean;
  isCoolingDegreeDaysChangeEnabled: boolean;
  isTxxEnabled: boolean;
  isTxxScoreEnabled: boolean;
  isTxxChangeEnabled: boolean;
  isClimateTEnabled: boolean;
  isClimateTScoreEnabled: boolean;
  isClimateTChangeEnabled: boolean;
  isFrostDaysEnabled: boolean;
  isFrostDaysScoreEnabled: boolean;
  isFrostDaysChangeEnabled: boolean;
  isHeatingDegreeDaysEnabled: boolean;
  isHeatingDegreeDaysScoreEnabled: boolean;
  isHeatingDegreeDaysChangeEnabled: boolean;
  isExtremeHotDaysEnabled: boolean;
  isExtremeHotDaysScoreEnabled: boolean;
  isExtremeHotDaysChangeEnabled: boolean;
  isVeryExtremeHotDaysEnabled: boolean;
  isVeryExtremeHotDaysScoreEnabled: boolean;
  isVeryExtremeHotDaysChangeEnabled: boolean;
  isClimatePrEnabled: boolean;
  isClimatePrScoreEnabled: boolean;
  isClimatePrChangeEnabled: boolean;
  isClimateCddEnabled: boolean;
  isClimateCddScoreEnabled: boolean;
  isClimateCddChangeEnabled: boolean;
  isClimateRx1dayEnabled: boolean;
  isClimateRx1dayScoreEnabled: boolean;
  isClimateRx1dayChangeEnabled: boolean;
  isClimateRx5dayEnabled: boolean;
  isClimateRx5dayScoreEnabled: boolean;
  isClimateRx5dayChangeEnabled: boolean;
  isClimatePrsnEnabled: boolean;
  isClimatePrsnScoreEnabled: boolean;
  isClimatePrsnChangeEnabled: boolean;
  isClimateSstEnabled: boolean;
  isClimateSstScoreEnabled: boolean;
  isClimateSstChangeEnabled: boolean;
  isClimateSfcWindEnabled: boolean;
  isClimateSfcWindScoreEnabled: boolean;
  isClimateSfcWindChangeEnabled: boolean;
  isClimateTrEnabled: boolean;
  isClimateTrScoreEnabled: boolean;
  isClimateTrChangeEnabled: boolean;
  isClimateSlrEnabled: boolean;
  isClimateSlrScoreEnabled: boolean;
  isClimateSlrChangeEnabled: boolean;
}

@Injectable()
export class DatapointsClimateChartService {

  locationMaxTemperatureSubscription: Subscription;
  locationMeanTemperatureSubscription: Subscription;
  locationMinTemperatureSubscription: Subscription;
  locationSnowfallSubscription: Subscription;
  locationPrecipitationSubscription: Subscription;
  locationSurfaceWindSubscription: Subscription;
  locationPopulationDensitySubscription: Subscription;
  locationSeaLevelRiseSubscription: Subscription;
  locationSeaLevelRiseWarmingSubscription: Subscription;
  locationSpi6Subscription: Subscription;
  locationSpi6ScoreSubscription: Subscription;
  locationSpi6ChangeSubscription: Subscription;
  locationCoolingDegreeDaysSubscription: Subscription;
  locationCoolingDegreeDaysScoreSubscription: Subscription;
  locationCoolingDegreeDaysChangeSubscription: Subscription;
  locationTxxSubscription: Subscription;
  locationTxxScoreSubscription: Subscription;
  locationTxxChangeSubscription: Subscription;
  locationClimateTSubscription: Subscription;
  locationClimateTScoreSubscription: Subscription;
  locationClimateTChangeSubscription: Subscription;
  locationFrostDaysSubscription: Subscription;
  locationFrostDaysScoreSubscription: Subscription;
  locationFrostDaysChangeSubscription: Subscription;
  locationHeatingDegreeDaysSubscription: Subscription;
  locationHeatingDegreeDaysScoreSubscription: Subscription;
  locationHeatingDegreeDaysChangeSubscription: Subscription;
  locationExtremeHotDaysSubscription: Subscription;
  locationExtremeHotDaysScoreSubscription: Subscription;
  locationExtremeHotDaysChangeSubscription: Subscription;
  locationVeryExtremeHotDaysSubscription: Subscription;
  locationVeryExtremeHotDaysScoreSubscription: Subscription;
  locationVeryExtremeHotDaysChangeSubscription: Subscription;
  locationClimatePrSubscription: Subscription;
  locationClimatePrScoreSubscription: Subscription;
  locationClimatePrChangeSubscription: Subscription;
  locationClimateCddSubscription: Subscription;
  locationClimateCddScoreSubscription: Subscription;
  locationClimateCddChangeSubscription: Subscription;
  locationClimateRx1daySubscription: Subscription;
  locationClimateRx1dayScoreSubscription: Subscription;
  locationClimateRx1dayChangeSubscription: Subscription;
  locationClimateRx5daySubscription: Subscription;
  locationClimateRx5dayScoreSubscription: Subscription;
  locationClimateRx5dayChangeSubscription: Subscription;
  locationClimatePrsnSubscription: Subscription;
  locationClimatePrsnScoreSubscription: Subscription;
  locationClimatePrsnChangeSubscription: Subscription;
  locationClimateSstSubscription: Subscription;
  locationClimateSstScoreSubscription: Subscription;
  locationClimateSstChangeSubscription: Subscription;
  locationClimateSfcWindSubscription: Subscription;
  locationClimateSfcWindScoreSubscription: Subscription;
  locationClimateSfcWindChangeSubscription: Subscription;
  locationClimateTrSubscription: Subscription;
  locationClimateTrScoreSubscription: Subscription;
  locationClimateTrChangeSubscription: Subscription;
  locationClimateSlrSubscription: Subscription;
  locationClimateSlrScoreSubscription: Subscription;
  locationClimateSlrChangeSubscription: Subscription;

  addressProfileMaxTemperatureSubscription: Subscription;
  addressProfileMeanTemperatureSubscription: Subscription;
  addressProfileMinTemperatureSubscription: Subscription;
  addressProfileSnowfallSubscription: Subscription;
  addressProfilePrecipitationSubscription: Subscription;
  addressProfileSurfaceWindSubscription: Subscription;
  addressProfilePopulationDensitySubscription: Subscription;
  addressProfileSeaLevelRiseSubscription: Subscription;
  addressProfileSeaLevelRiseWarmingSubscription: Subscription;
  addressProfileSpi6Subscription: Subscription;
  addressProfileSpi6ScoreSubscription: Subscription;
  addressProfileSpi6ChangeSubscription: Subscription;
  addressProfileCoolingDegreeDaysSubscription: Subscription;
  addressProfileCoolingDegreeDaysScoreSubscription: Subscription;
  addressProfileCoolingDegreeDaysChangeSubscription: Subscription;
  addressProfileTxxSubscription: Subscription;
  addressProfileTxxScoreSubscription: Subscription;
  addressProfileTxxChangeSubscription: Subscription;
  addressProfileClimateTSubscription: Subscription;
  addressProfileClimateTScoreSubscription: Subscription;
  addressProfileClimateTChangeSubscription: Subscription;
  addressProfileFrostDaysSubscription: Subscription;
  addressProfileFrostDaysScoreSubscription: Subscription;
  addressProfileFrostDaysChangeSubscription: Subscription;
  addressProfileHeatingDegreeDaysSubscription: Subscription;
  addressProfileHeatingDegreeDaysScoreSubscription: Subscription;
  addressProfileHeatingDegreeDaysChangeSubscription: Subscription;
  addressProfileExtremeHotDaysSubscription: Subscription;
  addressProfileExtremeHotDaysScoreSubscription: Subscription;
  addressProfileExtremeHotDaysChangeSubscription: Subscription;
  addressProfileVeryExtremeHotDaysSubscription: Subscription;
  addressProfileVeryExtremeHotDaysScoreSubscription: Subscription;
  addressProfileVeryExtremeHotDaysChangeSubscription: Subscription;
  addressProfileClimatePrSubscription: Subscription;
  addressProfileClimatePrScoreSubscription: Subscription;
  addressProfileClimatePrChangeSubscription: Subscription;
  addressProfileClimateCddSubscription: Subscription;
  addressProfileClimateCddScoreSubscription: Subscription;
  addressProfileClimateCddChangeSubscription: Subscription;
  addressProfileClimateRx1daySubscription: Subscription;
  addressProfileClimateRx1dayScoreSubscription: Subscription;
  addressProfileClimateRx1dayChangeSubscription: Subscription;
  addressProfileClimateRx5daySubscription: Subscription;
  addressProfileClimateRx5dayScoreSubscription: Subscription;
  addressProfileClimateRx5dayChangeSubscription: Subscription;
  addressProfileClimatePrsnSubscription: Subscription;
  addressProfileClimatePrsnScoreSubscription: Subscription;
  addressProfileClimatePrsnChangeSubscription: Subscription;
  addressProfileClimateSstSubscription: Subscription;
  addressProfileClimateSstScoreSubscription: Subscription;
  addressProfileClimateSstChangeSubscription: Subscription;
  addressProfileClimateSfcWindSubscription: Subscription;
  addressProfileClimateSfcWindScoreSubscription: Subscription;
  addressProfileClimateSfcWindChangeSubscription: Subscription;
  addressProfileClimateTrSubscription: Subscription;
  addressProfileClimateTrScoreSubscription: Subscription;
  addressProfileClimateTrChangeSubscription: Subscription;
  addressProfileClimateSlrSubscription: Subscription;
  addressProfileClimateSlrScoreSubscription: Subscription;
  addressProfileClimateSlrChangeSubscription: Subscription;

  downloadRequestPayload: {maxTemperature: ClimateChart, meanTemperature: ClimateChart, minTemperature: ClimateChart, snowfallTemperature: ClimateChart, precipitation: ClimateChart, surfaceWind: ClimateChart, populationDensity: ClimateChart, seaLevelRise: ClimateChart, seaLevelRiseWarming: ClimateChart} =
  {maxTemperature: null, meanTemperature: null, minTemperature: null, snowfallTemperature: null, precipitation: null, surfaceWind: null, populationDensity: null, seaLevelRise: null, seaLevelRiseWarming: null};

  /**
   * The Climate Change > Region tab's charts, one per bioclimatic indicator. A list rather than
   * graphType keys because how many there are depends on the zone the location falls in.
   */
  regionGraphs: {title: string, instance: any}[] = [];

  /** The same charts for Download Data, keyed so they follow the Risk Score blocks in the sheet. */
  regionDownloadPayload: {[key: string]: ClimateChart} = {};

  /** The bioclimatic zone of the open profile, for the profile data section of the chart PDF. */
  bioclimaticRegion: BioclimaticRegion | null = null;

  downloadClimatologyRequestPayload: {spi6: ClimateChart, spi6Score: ClimateChart, spi6Change: ClimateChart, coolingDegreeDays: ClimateChart, coolingDegreeDaysScore: ClimateChart, coolingDegreeDaysChange: ClimateChart, txx: ClimateChart, txxScore: ClimateChart, txxChange: ClimateChart, climateT: ClimateChart, climateTScore: ClimateChart, climateTChange: ClimateChart, frostDays: ClimateChart, frostDaysScore: ClimateChart, frostDaysChange: ClimateChart, heatingDegreeDays: ClimateChart, heatingDegreeDaysScore: ClimateChart, heatingDegreeDaysChange: ClimateChart, extremeHotDays: ClimateChart, extremeHotDaysScore: ClimateChart, extremeHotDaysChange: ClimateChart, veryExtremeHotDays: ClimateChart, veryExtremeHotDaysScore: ClimateChart, veryExtremeHotDaysChange: ClimateChart, climatePr: ClimateChart, climatePrScore: ClimateChart, climatePrChange: ClimateChart, climateCdd: ClimateChart, climateCddScore: ClimateChart, climateCddChange: ClimateChart, climateRx1day: ClimateChart, climateRx1dayScore: ClimateChart, climateRx1dayChange: ClimateChart, climateRx5day: ClimateChart, climateRx5dayScore: ClimateChart, climateRx5dayChange: ClimateChart, climatePrsn: ClimateChart, climatePrsnScore: ClimateChart, climatePrsnChange: ClimateChart, climateSst: ClimateChart, climateSstScore: ClimateChart, climateSstChange: ClimateChart, climateSfcWind: ClimateChart, climateSfcWindScore: ClimateChart, climateSfcWindChange: ClimateChart, climateTr: ClimateChart, climateTrScore: ClimateChart, climateTrChange: ClimateChart, climateSlr: ClimateChart, climateSlrScore: ClimateChart, climateSlrChange: ClimateChart} =
  {spi6: null, spi6Score: null, spi6Change: null, coolingDegreeDays: null, coolingDegreeDaysScore: null, coolingDegreeDaysChange: null, txx: null, txxScore: null, txxChange: null, climateT: null, climateTScore: null, climateTChange: null, frostDays: null, frostDaysScore: null, frostDaysChange: null, heatingDegreeDays: null, heatingDegreeDaysScore: null, heatingDegreeDaysChange: null, extremeHotDays: null, extremeHotDaysScore: null, extremeHotDaysChange: null, veryExtremeHotDays: null, veryExtremeHotDaysScore: null, veryExtremeHotDaysChange: null, climatePr: null, climatePrScore: null, climatePrChange: null, climateCdd: null, climateCddScore: null, climateCddChange: null, climateRx1day: null, climateRx1dayScore: null, climateRx1dayChange: null, climateRx5day: null, climateRx5dayScore: null, climateRx5dayChange: null, climatePrsn: null, climatePrsnScore: null, climatePrsnChange: null, climateSst: null, climateSstScore: null, climateSstChange: null, climateSfcWind: null, climateSfcWindScore: null, climateSfcWindChange: null, climateTr: null, climateTrScore: null, climateTrChange: null, climateSlr: null, climateSlrScore: null, climateSlrChange: null};

  chartGraphicsInstances: {maxTemperature: any, meanTemperature: any, minTemperature: any, snowfallTemperature: any, precipitation: any, surfaceWind: any, populationDensity: any, seaLevelRise: any, seaLevelRiseWarming: any, spi6: any, spi6Score: any, spi6Change: any, coolingDegreeDays: any, coolingDegreeDaysScore: any, coolingDegreeDaysChange: any, txx: any, txxScore: any, txxChange: any, climateT: any, climateTScore: any, climateTChange: any, frostDays: any, frostDaysScore: any, frostDaysChange: any, heatingDegreeDays: any, heatingDegreeDaysScore: any, heatingDegreeDaysChange: any, extremeHotDays: any, extremeHotDaysScore: any, extremeHotDaysChange: any, veryExtremeHotDays: any, veryExtremeHotDaysScore: any, veryExtremeHotDaysChange: any, climatePr: any, climatePrScore: any, climatePrChange: any, climateCdd: any, climateCddScore: any, climateCddChange: any, climateRx1day: any, climateRx1dayScore: any, climateRx1dayChange: any, climateRx5day: any, climateRx5dayScore: any, climateRx5dayChange: any, climatePrsn: any, climatePrsnScore: any, climatePrsnChange: any, climateSst: any, climateSstScore: any, climateSstChange: any, climateSfcWind: any, climateSfcWindScore: any, climateSfcWindChange: any, climateTr: any, climateTrScore: any, climateTrChange: any, climateSlr: any, climateSlrScore: any, climateSlrChange: any} =
  {maxTemperature: null, meanTemperature: null, minTemperature: null, snowfallTemperature: null, precipitation: null, surfaceWind: null, populationDensity: null, seaLevelRise: null, seaLevelRiseWarming: null, spi6: null, spi6Score: null, spi6Change: null, coolingDegreeDays: null, coolingDegreeDaysScore: null, coolingDegreeDaysChange: null, txx: null, txxScore: null, txxChange: null, climateT: null, climateTScore: null, climateTChange: null, frostDays: null, frostDaysScore: null, frostDaysChange: null, heatingDegreeDays: null, heatingDegreeDaysScore: null, heatingDegreeDaysChange: null, extremeHotDays: null, extremeHotDaysScore: null, extremeHotDaysChange: null, veryExtremeHotDays: null, veryExtremeHotDaysScore: null, veryExtremeHotDaysChange: null, climatePr: null, climatePrScore: null, climatePrChange: null, climateCdd: null, climateCddScore: null, climateCddChange: null, climateRx1day: null, climateRx1dayScore: null, climateRx1dayChange: null, climateRx5day: null, climateRx5dayScore: null, climateRx5dayChange: null, climatePrsn: null, climatePrsnScore: null, climatePrsnChange: null, climateSst: null, climateSstScore: null, climateSstChange: null, climateSfcWind: null, climateSfcWindScore: null, climateSfcWindChange: null, climateTr: null, climateTrScore: null, climateTrChange: null, climateSlr: null, climateSlrScore: null, climateSlrChange: null};

  /**
   * Consolidated risk score charts, one per vulnerability profile type. Kept as a list
   * rather than in chartGraphicsConfiguration.graphType because the profiles are configured
   * per account, so the set of charts is not a fixed list of keys. Same {title, instance}
   * shape, so the profile chart download appends them to the same PDF as the other charts.
   */
  consolidatedRiskScoreGraphs: {title: string, instance: any}[] = [];

  /**
   * The instant calculation result, when one has been run. It travels with the download request
   * because it is never stored, so unlike every other block in the Climate Change sheet
   * it cannot be read back from the datapoint.
   */
  consolidatedRiskScoreInstantChart: ClimateChart | null = null;

  chartGraphicsConfiguration: {  format: 'JPEG' | 'PNG' | 'WEBP',
                                 graphType:{
                                           maxTemperature: {title: string, instance: any}, meanTemperature: {title: string, instance: any}, minTemperature: {title: string, instance: any}, snowfallTemperature: {title: string, instance: any},
                                           precipitation: {title: string, instance: any}, surfaceWind: {title: string, instance: any}, populationDensity: {title: string, instance: any},
                                           seaLevelRise: {title: string, instance: any}, seaLevelRiseWarming: {title: string, instance: any}, spi6: {title: string, instance: any}, spi6Score: {title: string, instance: any}, spi6Change: {title: string, instance: any}, coolingDegreeDays: {title: string, instance: any}, coolingDegreeDaysScore: {title: string, instance: any}, coolingDegreeDaysChange: {title: string, instance: any}, txx: {title: string, instance: any}, txxScore: {title: string, instance: any}, txxChange: {title: string, instance: any}, climateT: {title: string, instance: any}, climateTScore: {title: string, instance: any}, climateTChange: {title: string, instance: any}, frostDays: {title: string, instance: any}, frostDaysScore: {title: string, instance: any}, frostDaysChange: {title: string, instance: any}, heatingDegreeDays: {title: string, instance: any}, heatingDegreeDaysScore: {title: string, instance: any}, heatingDegreeDaysChange: {title: string, instance: any}, extremeHotDays: {title: string, instance: any}, extremeHotDaysScore: {title: string, instance: any}, extremeHotDaysChange: {title: string, instance: any}, veryExtremeHotDays: {title: string, instance: any}, veryExtremeHotDaysScore: {title: string, instance: any}, veryExtremeHotDaysChange: {title: string, instance: any}, climatePr: {title: string, instance: any}, climatePrScore: {title: string, instance: any}, climatePrChange: {title: string, instance: any}, climateCdd: {title: string, instance: any}, climateCddScore: {title: string, instance: any}, climateCddChange: {title: string, instance: any}, climateRx1day: {title: string, instance: any}, climateRx1dayScore: {title: string, instance: any}, climateRx1dayChange: {title: string, instance: any}, climateRx5day: {title: string, instance: any}, climateRx5dayScore: {title: string, instance: any}, climateRx5dayChange: {title: string, instance: any}, climatePrsn: {title: string, instance: any}, climatePrsnScore: {title: string, instance: any}, climatePrsnChange: {title: string, instance: any}, climateSst: {title: string, instance: any}, climateSstScore: {title: string, instance: any}, climateSstChange: {title: string, instance: any}, climateSfcWind: {title: string, instance: any}, climateSfcWindScore: {title: string, instance: any}, climateSfcWindChange: {title: string, instance: any}, climateTr: {title: string, instance: any}, climateTrScore: {title: string, instance: any}, climateTrChange: {title: string, instance: any}, climateSlr: {title: string, instance: any}, climateSlrScore: {title: string, instance: any}, climateSlrChange: {title: string, instance: any}
                                         }
                              } =
    {
      format: 'PNG',
      graphType: {
                    maxTemperature: {title: 'Maximum Temperature', instance: null}, meanTemperature: {title: 'Mean Temperature', instance: null}, minTemperature: {title: 'Minimum Temperature', instance: null}, snowfallTemperature: {title: 'Snowfall', instance: null},
                    precipitation: {title: 'Precipitation', instance: null}, surfaceWind: {title: 'Surface Wind', instance: null}, populationDensity: {title: 'Population Density', instance: null},
                    seaLevelRise: {title: 'Sea Level Rise', instance: null}, seaLevelRiseWarming: {title: 'Sea Level Rise Warming', instance: null}, spi6: {title: 'Subsidence, Water Stress - Standardised Precipitation Index (SPI-6)', instance: null}, spi6Score: {title: 'Subsidence, Water Stress - Standardised Precipitation Index (SPI-6)', instance: null}, spi6Change: {title: 'Subsidence, Water Stress - Standardised Precipitation Index (SPI-6)', instance: null}, coolingDegreeDays: {title: 'Changing temperature air - Cooling degree days (CD)', instance: null}, coolingDegreeDaysScore: {title: 'Changing temperature air - Cooling degree days (CD)', instance: null}, coolingDegreeDaysChange: {title: 'Changing temperature air - Cooling degree days (CD)', instance: null}, txx: {title: 'Heat Stress - Maximum of daily maximum temperature (TXx)', instance: null}, txxScore: {title: 'Heat Stress - Maximum of daily maximum temperature (TXx)', instance: null}, txxChange: {title: 'Heat Stress - Maximum of daily maximum temperature (TXx)', instance: null}, climateT: {title: 'Changing temperature air, Changing temperature freshwater - Mean of daily mean temperature (T)', instance: null}, climateTScore: {title: 'Changing temperature air, Changing temperature freshwater - Mean of daily mean temperature (T)', instance: null}, climateTChange: {title: 'Changing temperature air, Changing temperature freshwater - Mean of daily mean temperature (T)', instance: null}, frostDays: {title: 'Cold wave/frost - Frost days (FD) (minimum temperature below 0°C)', instance: null}, frostDaysScore: {title: 'Cold wave/frost - Frost days (FD) (minimum temperature below 0°C)', instance: null}, frostDaysChange: {title: 'Cold wave/frost - Frost days (FD) (minimum temperature below 0°C)', instance: null}, heatingDegreeDays: {title: 'Changing temperature air - Heating degree days (HD)', instance: null}, heatingDegreeDaysScore: {title: 'Changing temperature air - Heating degree days (HD)', instance: null}, heatingDegreeDaysChange: {title: 'Changing temperature air - Heating degree days (HD)', instance: null}, extremeHotDays: {title: 'Heat wave - Number of days with maximum temperature above 35°C (Bias Adjusted TX35)', instance: null}, extremeHotDaysScore: {title: 'Heat wave - Number of days with maximum temperature above 35°C (Bias Adjusted TX35)', instance: null}, extremeHotDaysChange: {title: 'Heat wave - Number of days with maximum temperature above 35°C (Bias Adjusted TX35)', instance: null}, veryExtremeHotDays: {title: 'Heat wave - Number of days with maximum temperature above 40°C (Bias Adjusted TX40)', instance: null}, veryExtremeHotDaysScore: {title: 'Heat wave - Number of days with maximum temperature above 40°C (Bias Adjusted TX40)', instance: null}, veryExtremeHotDaysChange: {title: 'Heat wave - Number of days with maximum temperature above 40°C (Bias Adjusted TX40)', instance: null}, climatePr: {title: 'Changing precipitation patterns rain - Mean of daily accumulated precipitation (PR)', instance: null}, climatePrScore: {title: 'Changing precipitation patterns rain - Mean of daily accumulated precipitation (PR)', instance: null}, climatePrChange: {title: 'Changing precipitation patterns rain - Mean of daily accumulated precipitation (PR)', instance: null}, climateCdd: {title: 'Wildfire - Maximum consecutive dry days (CDD)', instance: null}, climateCddScore: {title: 'Wildfire - Maximum consecutive dry days (CDD)', instance: null}, climateCddChange: {title: 'Wildfire - Maximum consecutive dry days (CDD)', instance: null}, climateRx1day: {title: 'Flood, Heavy precipitation - Maximum of 1-day accumulated precipitation (RX1day)', instance: null}, climateRx1dayScore: {title: 'Flood, Heavy precipitation - Maximum of 1-day accumulated precipitation (RX1day)', instance: null}, climateRx1dayChange: {title: 'Flood, Heavy precipitation - Maximum of 1-day accumulated precipitation (RX1day)', instance: null}, climateRx5day: {title: 'Flood, Landslide, Subsidence - Maximum of 5-day accumulated precipitation (RX5day)', instance: null}, climateRx5dayScore: {title: 'Flood, Landslide, Subsidence - Maximum of 5-day accumulated precipitation (RX5day)', instance: null}, climateRx5dayChange: {title: 'Flood, Landslide, Subsidence - Maximum of 5-day accumulated precipitation (RX5day)', instance: null}, climatePrsn: {title: 'Changing precipitation patterns snow - Mean of daily accumulated snowfall precipitation (PRSN)', instance: null}, climatePrsnScore: {title: 'Changing precipitation patterns snow - Mean of daily accumulated snowfall precipitation (PRSN)', instance: null}, climatePrsnChange: {title: 'Changing precipitation patterns snow - Mean of daily accumulated snowfall precipitation (PRSN)', instance: null}, climateSst: {title: 'Changing Temperature Marine Water - Mean Sea Surface Temperature (SST)', instance: null}, climateSstScore: {title: 'Changing Temperature Marine Water - Mean Sea Surface Temperature (SST)', instance: null}, climateSstChange: {title: 'Changing Temperature Marine Water - Mean Sea Surface Temperature (SST)', instance: null}, climateSfcWind: {title: 'Changing Wind Patterns - Mean of daily mean wind speed (SFCWIND)', instance: null}, climateSfcWindScore: {title: 'Changing Wind Patterns - Mean of daily mean wind speed (SFCWIND)', instance: null}, climateSfcWindChange: {title: 'Changing Wind Patterns - Mean of daily mean wind speed (SFCWIND)', instance: null}, climateTr: {title: 'Tropical Nights - Number of days with minimum temperature above 20°C (TR)', instance: null}, climateTrScore: {title: 'Tropical Nights - Number of days with minimum temperature above 20°C (TR)', instance: null}, climateTrChange: {title: 'Tropical Nights - Number of days with minimum temperature above 20°C (TR)', instance: null}, climateSlr: {title: 'Sea Level Rise - Mean Sea Level Rise (SLR)', instance: null}, climateSlrScore: {title: 'Sea Level Rise - Mean Sea Level Rise (SLR)', instance: null}, climateSlrChange: {title: 'Sea Level Rise - Mean Sea Level Rise (SLR)', instance: null}
                  }
    };

  constructor(private climateService: ClimateService,
    public maxTemperatureService: MaxTemperatureService,
    public meanTemperatureService: MeanTemperatureService,
    public minTemperatureService: MinTemperatureService,
    public snowfallService: SnowfallService,
    public precipitationService: PrecipitationService,
    public surfaceWindService: SurfaceWindService,
    public populationDensityService: PopulationDensityService,
    public seaLevelRiseService: SeaLevelRiseService,
    public seaLevelRiseWarmingService: SeaLevelRiseWarmingService,
    public spi6Service: Spi6Service,
    public spi6ScoreService: Spi6ScoreService,
    public spi6ChangeService: Spi6ChangeService,
    public coolingDegreeDaysService: CoolingDegreeDaysService,
    public coolingDegreeDaysScoreService: CoolingDegreeDaysScoreService,
    public coolingDegreeDaysChangeService: CoolingDegreeDaysChangeService,
    public txxService: TxxService,
    public txxScoreService: TxxScoreService,
    public txxChangeService: TxxChangeService,
    public climateTService: ClimateTService,
    public climateTScoreService: ClimateTScoreService,
    public climateTChangeService: ClimateTChangeService,
    public frostDaysService: FrostDaysService,
    public frostDaysScoreService: FrostDaysScoreService,
    public frostDaysChangeService: FrostDaysChangeService,
    public heatingDegreeDaysService: HeatingDegreeDaysService,
    public heatingDegreeDaysScoreService: HeatingDegreeDaysScoreService,
    public heatingDegreeDaysChangeService: HeatingDegreeDaysChangeService,
    public extremeHotDaysService: ExtremeHotDaysService,
    public extremeHotDaysScoreService: ExtremeHotDaysScoreService,
    public extremeHotDaysChangeService: ExtremeHotDaysChangeService,
    public veryExtremeHotDaysService: VeryExtremeHotDaysService,
    public veryExtremeHotDaysScoreService: VeryExtremeHotDaysScoreService,
    public veryExtremeHotDaysChangeService: VeryExtremeHotDaysChangeService,
    public climatePrService: ClimatePrService,
    public climatePrScoreService: ClimatePrScoreService,
    public climatePrChangeService: ClimatePrChangeService,
    public climateCddService: ClimateCddService,
    public climateCddScoreService: ClimateCddScoreService,
    public climateCddChangeService: ClimateCddChangeService,
    public climateRx1dayService: ClimateRx1dayService,
    public climateRx1dayScoreService: ClimateRx1dayScoreService,
    public climateRx1dayChangeService: ClimateRx1dayChangeService,
    public climateRx5dayService: ClimateRx5dayService,
    public climateRx5dayScoreService: ClimateRx5dayScoreService,
    public climateRx5dayChangeService: ClimateRx5dayChangeService,
    public climatePrsnService: ClimatePrsnService,
    public climatePrsnScoreService: ClimatePrsnScoreService,
    public climatePrsnChangeService: ClimatePrsnChangeService,
    public climateSstService: ClimateSstService,
    public climateSstScoreService: ClimateSstScoreService,
    public climateSstChangeService: ClimateSstChangeService,
    public climateSfcWindService: ClimateSfcWindService,
    public climateSfcWindScoreService: ClimateSfcWindScoreService,
    public climateSfcWindChangeService: ClimateSfcWindChangeService,
    public climateTrService: ClimateTrService,
    public climateTrScoreService: ClimateTrScoreService,
    public climateTrChangeService: ClimateTrChangeService,
    public climateSlrService: ClimateSlrService,
    public climateSlrScoreService: ClimateSlrScoreService,
    public climateSlrChangeService: ClimateSlrChangeService) {

  }

  /**
   * Validates if chart data contains actual data to display
   * Returns false if chartConfig is null or series object is empty
   * @param climateChart - The climate chart data to validate
   * @returns true if chart has valid data, false otherwise
   */
  private isChartDataValid(climateChart: ClimateChart): boolean {
    if (!climateChart) {
      return false;
    }

    // Check if chartConfig is null
    if (!climateChart.chartConfig) {
      return false;
    }

    // Check if series object is empty
    if (!climateChart.series || Object.keys(climateChart.series).length === 0) {
      return false;
    }

    // Check if legends and xAxis arrays are empty
    if (!climateChart.legends || climateChart.legends.length === 0) {
      return false;
    }

    if (!climateChart.xAxis || climateChart.xAxis.length === 0) {
      return false;
    }

    return true;
  }

  showLoading(ec) {
    ec.showLoading({
      text: 'Loading',
      color: MAPTYCS_CHART_COLOR,
      textColor: '#000',
      maskColor: 'rgba(255, 255, 255, 0.8)',
      zlevel: 0,

      // Font size. Available since `v4.8.0`.
      fontSize: 12,
      // Show an animated "spinner" or not. Available since `v4.8.0`.
      showSpinner: true,
      // Radius of the "spinner". Available since `v4.8.0`.
      spinnerRadius: 10,
      // Line width of the "spinner". Available since `v4.8.0`.
      lineWidth: 5,
      // Font thick weight. Available since `v5.0.1`.
      fontWeight: 'normal',
      // Font style. Available since `v5.0.1`.
      fontStyle: 'normal',
      // Font family. Available since `v5.0.1`.
      fontFamily: 'sans-serif'
    });
  }

  checkClimatePermissions(climateOverlayPermission: ClimateOverlayPermission, datasetID: string) {
    this.resetDownloadPayload();
    this.climateService.isMaxTemperatureEnabled(datasetID).subscribe(response => { climateOverlayPermission.isMaxTemperatureEnabled = response });
    this.climateService.isMeanTemperatureEnabled(datasetID).subscribe(response => { climateOverlayPermission.isMeanTemperatureEnabled = response });
    this.climateService.isMinTemperatureEnabled(datasetID).subscribe(response => { climateOverlayPermission.isMinTemperatureEnabled = response });
    this.climateService.isSnowFallEnabled(datasetID).subscribe(response => { climateOverlayPermission.isSnowFallEnabled = response });
    this.climateService.isPrecipitationEnabled(datasetID).subscribe(response => { climateOverlayPermission.isPrecipitationEnabled = response });
    this.climateService.isSurfaceWindEnabled(datasetID).subscribe(response => { climateOverlayPermission.isSurfaceWindEnabled = response });
    this.climateService.isPopulationDensityEnabled(datasetID).subscribe(response => { climateOverlayPermission.isPopulationDensityEnabled = response });
    this.climateService.isSeaLevelRiseEnabled(datasetID).subscribe(response => { climateOverlayPermission.isSeaLevelRiseEnabled = response });
    this.climateService.isSeaLevelRiseWarmingEnabled(datasetID).subscribe(response => { climateOverlayPermission.isSeaLevelRiseWarmingEnabled = response });
    this.climateService.isCoolingDegreeDaysEnabled(datasetID).subscribe(response => { climateOverlayPermission.isCoolingDegreeDaysEnabled = response });
    this.climateService.isCoolingDegreeDaysScoreEnabled(datasetID).subscribe(response => { climateOverlayPermission.isCoolingDegreeDaysScoreEnabled = response });
  }

  checkClimatologyPermissions(climateOverlayPermission: ClimateOverlayPermission, datasetID: string, onComplete?: () => void) {
    this.resetDownloadClimatologyPayload();
    
    // Use forkJoin to wait for all permission checks to complete
    // Each permission check has catchError to default to false if API fails
    const permissionChecks = [
      this.climateService.isSpi6Enabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isSpi6Enabled = response }),
        catchError(() => { climateOverlayPermission.isSpi6Enabled = false; return of(false); })
      ),
      this.climateService.isSpi6ScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isSpi6ScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isSpi6ScoreEnabled = false; return of(false); })
      ),
      this.climateService.isSpi6ChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isSpi6ChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isSpi6ChangeEnabled = false; return of(false); })
      ),
      this.climateService.isCoolingDegreeDaysEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isCoolingDegreeDaysEnabled = response }),
        catchError(() => { climateOverlayPermission.isCoolingDegreeDaysEnabled = false; return of(false); })
      ),
      this.climateService.isCoolingDegreeDaysScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isCoolingDegreeDaysScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isCoolingDegreeDaysScoreEnabled = false; return of(false); })
      ),
      this.climateService.isCoolingDegreeDaysChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isCoolingDegreeDaysChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isCoolingDegreeDaysChangeEnabled = false; return of(false); })
      ),
      this.climateService.isTxxEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isTxxEnabled = response }),
        catchError(() => { climateOverlayPermission.isTxxEnabled = false; return of(false); })
      ),
      this.climateService.isTxxScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isTxxScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isTxxScoreEnabled = false; return of(false); })
      ),
      this.climateService.isTxxChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isTxxChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isTxxChangeEnabled = false; return of(false); })
      ),
      this.climateService.isClimateTEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateTEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateTEnabled = false; return of(false); })
      ),
      this.climateService.isClimateTScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateTScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateTScoreEnabled = false; return of(false); })
      ),
      this.climateService.isClimateTChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateTChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateTChangeEnabled = false; return of(false); })
      ),
      this.climateService.isFrostDaysEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isFrostDaysEnabled = response }),
        catchError(() => { climateOverlayPermission.isFrostDaysEnabled = false; return of(false); })
      ),
      this.climateService.isFrostDaysScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isFrostDaysScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isFrostDaysScoreEnabled = false; return of(false); })
      ),
      this.climateService.isFrostDaysChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isFrostDaysChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isFrostDaysChangeEnabled = false; return of(false); })
      ),
      this.climateService.isHeatingDegreeDaysEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isHeatingDegreeDaysEnabled = response }),
        catchError(() => { climateOverlayPermission.isHeatingDegreeDaysEnabled = false; return of(false); })
      ),
      this.climateService.isHeatingDegreeDaysScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isHeatingDegreeDaysScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isHeatingDegreeDaysScoreEnabled = false; return of(false); })
      ),
      this.climateService.isHeatingDegreeDaysChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isHeatingDegreeDaysChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isHeatingDegreeDaysChangeEnabled = false; return of(false); })
      ),
      this.climateService.isExtremeHotDaysEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isExtremeHotDaysEnabled = response }),
        catchError(() => { climateOverlayPermission.isExtremeHotDaysEnabled = false; return of(false); })
      ),
      this.climateService.isExtremeHotDaysScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isExtremeHotDaysScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isExtremeHotDaysScoreEnabled = false; return of(false); })
      ),
      this.climateService.isExtremeHotDaysChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isExtremeHotDaysChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isExtremeHotDaysChangeEnabled = false; return of(false); })
      ),
      this.climateService.isVeryExtremeHotDaysEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isVeryExtremeHotDaysEnabled = response }),
        catchError(() => { climateOverlayPermission.isVeryExtremeHotDaysEnabled = false; return of(false); })
      ),
      this.climateService.isVeryExtremeHotDaysScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isVeryExtremeHotDaysScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isVeryExtremeHotDaysScoreEnabled = false; return of(false); })
      ),
      this.climateService.isVeryExtremeHotDaysChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isVeryExtremeHotDaysChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isVeryExtremeHotDaysChangeEnabled = false; return of(false); })
      ),
      this.climateService.isClimatePrEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimatePrEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimatePrEnabled = false; return of(false); })
      ),
      this.climateService.isClimatePrScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimatePrScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimatePrScoreEnabled = false; return of(false); })
      ),
      this.climateService.isClimatePrChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimatePrChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimatePrChangeEnabled = false; return of(false); })
      ),
      this.climateService.isClimateCddEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateCddEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateCddEnabled = false; return of(false); })
      ),
      this.climateService.isClimateCddScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateCddScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateCddScoreEnabled = false; return of(false); })
      ),
      this.climateService.isClimateCddChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateCddChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateCddChangeEnabled = false; return of(false); })
      ),
      this.climateService.isClimateRx1dayEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateRx1dayEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateRx1dayEnabled = false; return of(false); })
      ),
      this.climateService.isClimateRx1dayScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateRx1dayScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateRx1dayScoreEnabled = false; return of(false); })
      ),
      this.climateService.isClimateRx1dayChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateRx1dayChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateRx1dayChangeEnabled = false; return of(false); })
      ),
      this.climateService.isClimateRx5dayEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateRx5dayEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateRx5dayEnabled = false; return of(false); })
      ),
      this.climateService.isClimateRx5dayScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateRx5dayScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateRx5dayScoreEnabled = false; return of(false); })
      ),
      this.climateService.isClimateRx5dayChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateRx5dayChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateRx5dayChangeEnabled = false; return of(false); })
      ),
      this.climateService.isClimatePrsnEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimatePrsnEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimatePrsnEnabled = false; return of(false); })
      ),
      this.climateService.isClimatePrsnScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimatePrsnScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimatePrsnScoreEnabled = false; return of(false); })
      ),
      this.climateService.isClimatePrsnChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimatePrsnChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimatePrsnChangeEnabled = false; return of(false); })
      ),
      this.climateService.isClimateSstEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateSstEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateSstEnabled = false; return of(false); })
      ),
      this.climateService.isClimateSstScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateSstScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateSstScoreEnabled = false; return of(false); })
      ),
      this.climateService.isClimateSstChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateSstChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateSstChangeEnabled = false; return of(false); })
      ),
      this.climateService.isClimateSfcWindEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateSfcWindEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateSfcWindEnabled = false; return of(false); })
      ),
      this.climateService.isClimateSfcWindScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateSfcWindScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateSfcWindScoreEnabled = false; return of(false); })
      ),
      this.climateService.isClimateSfcWindChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateSfcWindChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateSfcWindChangeEnabled = false; return of(false); })
      ),
      this.climateService.isClimateTrEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateTrEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateTrEnabled = false; return of(false); })
      ),
      this.climateService.isClimateTrScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateTrScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateTrScoreEnabled = false; return of(false); })
      ),
      this.climateService.isClimateTrChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateTrChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateTrChangeEnabled = false; return of(false); })
      ),
      this.climateService.isClimateSlrEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateSlrEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateSlrEnabled = false; return of(false); })
      ),
      this.climateService.isClimateSlrScoreEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateSlrScoreEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateSlrScoreEnabled = false; return of(false); })
      ),
      this.climateService.isClimateSlrChangeEnabled(datasetID).pipe(
        tap(response => { climateOverlayPermission.isClimateSlrChangeEnabled = response }),
        catchError(() => { climateOverlayPermission.isClimateSlrChangeEnabled = false; return of(false); })
      )
    ];
    
    forkJoin(permissionChecks).subscribe(() => {
      if (onComplete) {
        onComplete();
      }
    });
  }

  // Re-subscribe to climatology charts if permission is enabled but chart not initialized
  resubscribeClimatologyChartsIfNeeded(location: Point, datasetID: string, isLocationProfile: boolean = true) {
    if (isLocationProfile) {
      // CLIMATOLOGY tab charts
      if (!this.chartGraphicsConfiguration.graphType.spi6.instance && !this.locationSpi6Subscription) {
        this.locationSpi6Subscription = this.spi6Service.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchSpi6ByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.coolingDegreeDays.instance && !this.locationCoolingDegreeDaysSubscription) {
        this.locationCoolingDegreeDaysSubscription = this.coolingDegreeDaysService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchCoolingDegreeDaysByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.txx.instance && !this.locationTxxSubscription) {
        this.locationTxxSubscription = this.txxService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchTxxByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateT.instance && !this.locationClimateTSubscription) {
        this.locationClimateTSubscription = this.climateTService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.frostDays.instance && !this.locationFrostDaysSubscription) {
        this.locationFrostDaysSubscription = this.frostDaysService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchFrostDaysByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.heatingDegreeDays.instance && !this.locationHeatingDegreeDaysSubscription) {
        this.locationHeatingDegreeDaysSubscription = this.heatingDegreeDaysService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchHeatingDegreeDaysByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.extremeHotDays.instance && !this.locationExtremeHotDaysSubscription) {
        this.locationExtremeHotDaysSubscription = this.extremeHotDaysService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchExtremeHotDaysByLocation(location, datasetID, instance);
        });
      }
      
      // CHANGE tab charts
      if (!this.chartGraphicsConfiguration.graphType.spi6Change.instance && !this.locationSpi6ChangeSubscription) {
        this.locationSpi6ChangeSubscription = this.spi6ChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchSpi6ChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.coolingDegreeDaysChange.instance && !this.locationCoolingDegreeDaysChangeSubscription) {
        this.locationCoolingDegreeDaysChangeSubscription = this.coolingDegreeDaysChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchCoolingDegreeDaysChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.txxChange.instance && !this.locationTxxChangeSubscription) {
        this.locationTxxChangeSubscription = this.txxChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchTxxChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateTChange.instance && !this.locationClimateTChangeSubscription) {
        this.locationClimateTChangeSubscription = this.climateTChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.frostDaysChange.instance && !this.locationFrostDaysChangeSubscription) {
        this.locationFrostDaysChangeSubscription = this.frostDaysChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchFrostDaysChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.heatingDegreeDaysChange.instance && !this.locationHeatingDegreeDaysChangeSubscription) {
        this.locationHeatingDegreeDaysChangeSubscription = this.heatingDegreeDaysChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchHeatingDegreeDaysChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.extremeHotDaysChange.instance && !this.locationExtremeHotDaysChangeSubscription) {
        this.locationExtremeHotDaysChangeSubscription = this.extremeHotDaysChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchExtremeHotDaysChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.veryExtremeHotDaysChange.instance && !this.locationVeryExtremeHotDaysChangeSubscription) {
        this.locationVeryExtremeHotDaysChangeSubscription = this.veryExtremeHotDaysChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchVeryExtremeHotDaysChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePrChange.instance && !this.locationClimatePrChangeSubscription) {
        this.locationClimatePrChangeSubscription = this.climatePrChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateCddChange.instance && !this.locationClimateCddChangeSubscription) {
        this.locationClimateCddChangeSubscription = this.climateCddChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateCddChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx1day.instance && !this.locationClimateRx1daySubscription) {
        this.locationClimateRx1daySubscription = this.climateRx1dayService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx1dayByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx1dayChange.instance && !this.locationClimateRx1dayChangeSubscription) {
        this.locationClimateRx1dayChangeSubscription = this.climateRx1dayChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx1dayChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx5day.instance && !this.locationClimateRx5daySubscription) {
        this.locationClimateRx5daySubscription = this.climateRx5dayService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx5dayByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx5dayChange.instance && !this.locationClimateRx5dayChangeSubscription) {
        this.locationClimateRx5dayChangeSubscription = this.climateRx5dayChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx5dayChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePrsn.instance && !this.locationClimatePrsnSubscription) {
        this.locationClimatePrsnSubscription = this.climatePrsnService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrsnByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePrsnChange.instance && !this.locationClimatePrsnChangeSubscription) {
        this.locationClimatePrsnChangeSubscription = this.climatePrsnChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrsnChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSst.instance && !this.locationClimateSstSubscription) {
        this.locationClimateSstSubscription = this.climateSstService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSstByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSstChange.instance && !this.locationClimateSstChangeSubscription) {
        this.locationClimateSstChangeSubscription = this.climateSstChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSstChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSfcWind.instance && !this.locationClimateSfcWindSubscription) {
        this.locationClimateSfcWindSubscription = this.climateSfcWindService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSfcWindByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSfcWindChange.instance && !this.locationClimateSfcWindChangeSubscription) {
        this.locationClimateSfcWindChangeSubscription = this.climateSfcWindChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSfcWindChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateTr.instance && !this.locationClimateTrSubscription) {
        this.locationClimateTrSubscription = this.climateTrService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTrByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateTrChange.instance && !this.locationClimateTrChangeSubscription) {
        this.locationClimateTrChangeSubscription = this.climateTrChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTrChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSlr.instance && !this.locationClimateSlrSubscription) {
        this.locationClimateSlrSubscription = this.climateSlrService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSlrByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSlrChange.instance && !this.locationClimateSlrChangeSubscription) {
        this.locationClimateSlrChangeSubscription = this.climateSlrChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSlrChangeByLocation(location, datasetID, instance);
        });
      }
      
      // RISK_SCORING tab charts
      if (!this.chartGraphicsConfiguration.graphType.spi6Score.instance && !this.locationSpi6ScoreSubscription) {
        this.locationSpi6ScoreSubscription = this.spi6ScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchSpi6ScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.coolingDegreeDaysScore.instance && !this.locationCoolingDegreeDaysScoreSubscription) {
        this.locationCoolingDegreeDaysScoreSubscription = this.coolingDegreeDaysScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchCoolingDegreeDaysScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.txxScore.instance && !this.locationTxxScoreSubscription) {
        this.locationTxxScoreSubscription = this.txxScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchTxxScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateTScore.instance && !this.locationClimateTScoreSubscription) {
        this.locationClimateTScoreSubscription = this.climateTScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.frostDaysScore.instance && !this.locationFrostDaysScoreSubscription) {
        this.locationFrostDaysScoreSubscription = this.frostDaysScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchFrostDaysScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.heatingDegreeDaysScore.instance && !this.locationHeatingDegreeDaysScoreSubscription) {
        this.locationHeatingDegreeDaysScoreSubscription = this.heatingDegreeDaysScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchHeatingDegreeDaysScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.extremeHotDaysScore.instance && !this.locationExtremeHotDaysScoreSubscription) {
        this.locationExtremeHotDaysScoreSubscription = this.extremeHotDaysScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchExtremeHotDaysScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.veryExtremeHotDays.instance && !this.locationVeryExtremeHotDaysSubscription) {
        this.locationVeryExtremeHotDaysSubscription = this.veryExtremeHotDaysService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchVeryExtremeHotDaysByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.veryExtremeHotDaysScore.instance && !this.locationVeryExtremeHotDaysScoreSubscription) {
        this.locationVeryExtremeHotDaysScoreSubscription = this.veryExtremeHotDaysScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchVeryExtremeHotDaysScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePr.instance && !this.locationClimatePrSubscription) {
        this.locationClimatePrSubscription = this.climatePrService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePrScore.instance && !this.locationClimatePrScoreSubscription) {
        this.locationClimatePrScoreSubscription = this.climatePrScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateCdd.instance && !this.locationClimateCddSubscription) {
        this.locationClimateCddSubscription = this.climateCddService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateCddByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateCddScore.instance && !this.locationClimateCddScoreSubscription) {
        this.locationClimateCddScoreSubscription = this.climateCddScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateCddScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx1dayScore.instance && !this.locationClimateRx1dayScoreSubscription) {
        this.locationClimateRx1dayScoreSubscription = this.climateRx1dayScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx1dayScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx5dayScore.instance && !this.locationClimateRx5dayScoreSubscription) {
        this.locationClimateRx5dayScoreSubscription = this.climateRx5dayScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx5dayScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePrsnScore.instance && !this.locationClimatePrsnScoreSubscription) {
        this.locationClimatePrsnScoreSubscription = this.climatePrsnScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrsnScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSstScore.instance && !this.locationClimateSstScoreSubscription) {
        this.locationClimateSstScoreSubscription = this.climateSstScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSstScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSfcWindScore.instance && !this.locationClimateSfcWindScoreSubscription) {
        this.locationClimateSfcWindScoreSubscription = this.climateSfcWindScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSfcWindScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateTrScore.instance && !this.locationClimateTrScoreSubscription) {
        this.locationClimateTrScoreSubscription = this.climateTrScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTrScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSlrScore.instance && !this.locationClimateSlrScoreSubscription) {
        this.locationClimateSlrScoreSubscription = this.climateSlrScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSlrScoreByLocation(location, datasetID, instance);
        });
      }
    } else {
      // Address profile - CLIMATOLOGY tab charts
      if (!this.chartGraphicsConfiguration.graphType.spi6.instance && !this.addressProfileSpi6Subscription) {
        this.addressProfileSpi6Subscription = this.spi6Service.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchSpi6ByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.coolingDegreeDays.instance && !this.addressProfileCoolingDegreeDaysSubscription) {
        this.addressProfileCoolingDegreeDaysSubscription = this.coolingDegreeDaysService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchCoolingDegreeDaysByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.txx.instance && !this.addressProfileTxxSubscription) {
        this.addressProfileTxxSubscription = this.txxService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchTxxByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateT.instance && !this.addressProfileClimateTSubscription) {
        this.addressProfileClimateTSubscription = this.climateTService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.frostDays.instance && !this.addressProfileFrostDaysSubscription) {
        this.addressProfileFrostDaysSubscription = this.frostDaysService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchFrostDaysByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.heatingDegreeDays.instance && !this.addressProfileHeatingDegreeDaysSubscription) {
        this.addressProfileHeatingDegreeDaysSubscription = this.heatingDegreeDaysService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchHeatingDegreeDaysByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.extremeHotDays.instance && !this.addressProfileExtremeHotDaysSubscription) {
        this.addressProfileExtremeHotDaysSubscription = this.extremeHotDaysService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchExtremeHotDaysByLocation(location, datasetID, instance);
        });
      }
      
      // CHANGE tab charts
      if (!this.chartGraphicsConfiguration.graphType.spi6Change.instance && !this.addressProfileSpi6ChangeSubscription) {
        this.addressProfileSpi6ChangeSubscription = this.spi6ChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchSpi6ChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.coolingDegreeDaysChange.instance && !this.addressProfileCoolingDegreeDaysChangeSubscription) {
        this.addressProfileCoolingDegreeDaysChangeSubscription = this.coolingDegreeDaysChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchCoolingDegreeDaysChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.txxChange.instance && !this.addressProfileTxxChangeSubscription) {
        this.addressProfileTxxChangeSubscription = this.txxChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchTxxChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateTChange.instance && !this.addressProfileClimateTChangeSubscription) {
        this.addressProfileClimateTChangeSubscription = this.climateTChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.frostDaysChange.instance && !this.addressProfileFrostDaysChangeSubscription) {
        this.addressProfileFrostDaysChangeSubscription = this.frostDaysChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchFrostDaysChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.heatingDegreeDaysChange.instance && !this.addressProfileHeatingDegreeDaysChangeSubscription) {
        this.addressProfileHeatingDegreeDaysChangeSubscription = this.heatingDegreeDaysChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchHeatingDegreeDaysChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.extremeHotDaysChange.instance && !this.addressProfileExtremeHotDaysChangeSubscription) {
        this.addressProfileExtremeHotDaysChangeSubscription = this.extremeHotDaysChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchExtremeHotDaysChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.veryExtremeHotDaysChange.instance && !this.addressProfileVeryExtremeHotDaysChangeSubscription) {
        this.addressProfileVeryExtremeHotDaysChangeSubscription = this.veryExtremeHotDaysChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchVeryExtremeHotDaysChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePrChange.instance && !this.addressProfileClimatePrChangeSubscription) {
        this.addressProfileClimatePrChangeSubscription = this.climatePrChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateCddChange.instance && !this.addressProfileClimateCddChangeSubscription) {
        this.addressProfileClimateCddChangeSubscription = this.climateCddChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateCddChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx1day.instance && !this.addressProfileClimateRx1daySubscription) {
        this.addressProfileClimateRx1daySubscription = this.climateRx1dayService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx1dayByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx1dayChange.instance && !this.addressProfileClimateRx1dayChangeSubscription) {
        this.addressProfileClimateRx1dayChangeSubscription = this.climateRx1dayChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx1dayChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx5day.instance && !this.addressProfileClimateRx5daySubscription) {
        this.addressProfileClimateRx5daySubscription = this.climateRx5dayService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx5dayByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx5dayChange.instance && !this.addressProfileClimateRx5dayChangeSubscription) {
        this.addressProfileClimateRx5dayChangeSubscription = this.climateRx5dayChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx5dayChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePrsn.instance && !this.addressProfileClimatePrsnSubscription) {
        this.addressProfileClimatePrsnSubscription = this.climatePrsnService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrsnByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePrsnChange.instance && !this.addressProfileClimatePrsnChangeSubscription) {
        this.addressProfileClimatePrsnChangeSubscription = this.climatePrsnChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrsnChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSst.instance && !this.addressProfileClimateSstSubscription) {
        this.addressProfileClimateSstSubscription = this.climateSstService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSstByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSstChange.instance && !this.addressProfileClimateSstChangeSubscription) {
        this.addressProfileClimateSstChangeSubscription = this.climateSstChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSstChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSfcWind.instance && !this.addressProfileClimateSfcWindSubscription) {
        this.addressProfileClimateSfcWindSubscription = this.climateSfcWindService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSfcWindByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSfcWindChange.instance && !this.addressProfileClimateSfcWindChangeSubscription) {
        this.addressProfileClimateSfcWindChangeSubscription = this.climateSfcWindChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSfcWindChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateTr.instance && !this.addressProfileClimateTrSubscription) {
        this.addressProfileClimateTrSubscription = this.climateTrService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTrByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateTrChange.instance && !this.addressProfileClimateTrChangeSubscription) {
        this.addressProfileClimateTrChangeSubscription = this.climateTrChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTrChangeByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSlr.instance && !this.addressProfileClimateSlrSubscription) {
        this.addressProfileClimateSlrSubscription = this.climateSlrService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSlrByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSlrChange.instance && !this.addressProfileClimateSlrChangeSubscription) {
        this.addressProfileClimateSlrChangeSubscription = this.climateSlrChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSlrChangeByLocation(location, datasetID, instance);
        });
      }
      
      // RISK_SCORING tab charts
      if (!this.chartGraphicsConfiguration.graphType.spi6Score.instance && !this.addressProfileSpi6ScoreSubscription) {
        this.addressProfileSpi6ScoreSubscription = this.spi6ScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchSpi6ScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.coolingDegreeDaysScore.instance && !this.addressProfileCoolingDegreeDaysScoreSubscription) {
        this.addressProfileCoolingDegreeDaysScoreSubscription = this.coolingDegreeDaysScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchCoolingDegreeDaysScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.txxScore.instance && !this.addressProfileTxxScoreSubscription) {
        this.addressProfileTxxScoreSubscription = this.txxScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchTxxScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateTScore.instance && !this.addressProfileClimateTScoreSubscription) {
        this.addressProfileClimateTScoreSubscription = this.climateTScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.frostDaysScore.instance && !this.addressProfileFrostDaysScoreSubscription) {
        this.addressProfileFrostDaysScoreSubscription = this.frostDaysScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchFrostDaysScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.heatingDegreeDaysScore.instance && !this.addressProfileHeatingDegreeDaysScoreSubscription) {
        this.addressProfileHeatingDegreeDaysScoreSubscription = this.heatingDegreeDaysScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchHeatingDegreeDaysScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.extremeHotDaysScore.instance && !this.addressProfileExtremeHotDaysScoreSubscription) {
        this.addressProfileExtremeHotDaysScoreSubscription = this.extremeHotDaysScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchExtremeHotDaysScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.veryExtremeHotDays.instance && !this.addressProfileVeryExtremeHotDaysSubscription) {
        this.addressProfileVeryExtremeHotDaysSubscription = this.veryExtremeHotDaysService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchVeryExtremeHotDaysByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.veryExtremeHotDaysScore.instance && !this.addressProfileVeryExtremeHotDaysScoreSubscription) {
        this.addressProfileVeryExtremeHotDaysScoreSubscription = this.veryExtremeHotDaysScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchVeryExtremeHotDaysScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePr.instance && !this.addressProfileClimatePrSubscription) {
        this.addressProfileClimatePrSubscription = this.climatePrService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePrScore.instance && !this.addressProfileClimatePrScoreSubscription) {
        this.addressProfileClimatePrScoreSubscription = this.climatePrScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateCdd.instance && !this.addressProfileClimateCddSubscription) {
        this.addressProfileClimateCddSubscription = this.climateCddService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateCddByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateCddScore.instance && !this.addressProfileClimateCddScoreSubscription) {
        this.addressProfileClimateCddScoreSubscription = this.climateCddScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateCddScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx1dayScore.instance && !this.addressProfileClimateRx1dayScoreSubscription) {
        this.addressProfileClimateRx1dayScoreSubscription = this.climateRx1dayScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx1dayScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateRx5dayScore.instance && !this.addressProfileClimateRx5dayScoreSubscription) {
        this.addressProfileClimateRx5dayScoreSubscription = this.climateRx5dayScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateRx5dayScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climatePrsnScore.instance && !this.addressProfileClimatePrsnScoreSubscription) {
        this.addressProfileClimatePrsnScoreSubscription = this.climatePrsnScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimatePrsnScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSstScore.instance && !this.addressProfileClimateSstScoreSubscription) {
        this.addressProfileClimateSstScoreSubscription = this.climateSstScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSstScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSfcWindScore.instance && !this.addressProfileClimateSfcWindScoreSubscription) {
        this.addressProfileClimateSfcWindScoreSubscription = this.climateSfcWindScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSfcWindScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateTrScore.instance && !this.addressProfileClimateTrScoreSubscription) {
        this.addressProfileClimateTrScoreSubscription = this.climateTrScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateTrScoreByLocation(location, datasetID, instance);
        });
      }
      
      if (!this.chartGraphicsConfiguration.graphType.climateSlrScore.instance && !this.addressProfileClimateSlrScoreSubscription) {
        this.addressProfileClimateSlrScoreSubscription = this.climateSlrScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
          this.fetchClimateSlrScoreByLocation(location, datasetID, instance);
        });
      }
    }
  }

  subscribeLocationChartInstances(location: Point, datasetID: string) {
    this.locationMaxTemperatureSubscription = this.maxTemperatureService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchMaxTemperatureByLocation(location, datasetID, instance);
    });

    this.locationMeanTemperatureSubscription = this.meanTemperatureService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchMeanTemperatureByLocation(location, datasetID, instance);
    });

    this.locationMinTemperatureSubscription = this.minTemperatureService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchMinTemperatureByLocation(location, datasetID, instance);
    });

    this.locationSnowfallSubscription = this.snowfallService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSnowfallByLocation(location, datasetID, instance);
    });

    this.locationPrecipitationSubscription = this.precipitationService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchPrecipitationByLocation(location, datasetID, instance);
    });

    this.locationSurfaceWindSubscription = this.surfaceWindService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSurfacewindByLocation(location, datasetID, instance);
    });

    this.locationPopulationDensitySubscription = this.populationDensityService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchPopulationDensityByLocation(location, datasetID, instance);
    });

    this.locationSeaLevelRiseSubscription = this.seaLevelRiseService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSeaLevelRiseByLocation(location, datasetID, instance);
    });

    this.locationSeaLevelRiseWarmingSubscription = this.seaLevelRiseWarmingService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSeaLevelRiseWarmingByLocation(location, datasetID, instance);
    });
  }

  subscribeLocationClimatologyChartInstances(location: Point, datasetID: string, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.locationSpi6Subscription = this.spi6Service.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSpi6ByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationSpi6ScoreSubscription = this.spi6ScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSpi6ScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationSpi6ChangeSubscription = this.spi6ChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSpi6ChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationCoolingDegreeDaysSubscription = this.coolingDegreeDaysService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchCoolingDegreeDaysByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationCoolingDegreeDaysScoreSubscription = this.coolingDegreeDaysScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchCoolingDegreeDaysScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationCoolingDegreeDaysChangeSubscription = this.coolingDegreeDaysChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchCoolingDegreeDaysChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationTxxSubscription = this.txxService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchTxxByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationTxxScoreSubscription = this.txxScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchTxxScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationTxxChangeSubscription = this.txxChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchTxxChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateTSubscription = this.climateTService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateTScoreSubscription = this.climateTScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateTChangeSubscription = this.climateTChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationFrostDaysSubscription = this.frostDaysService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchFrostDaysByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationFrostDaysScoreSubscription = this.frostDaysScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchFrostDaysScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationFrostDaysChangeSubscription = this.frostDaysChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchFrostDaysChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationHeatingDegreeDaysSubscription = this.heatingDegreeDaysService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchHeatingDegreeDaysByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationHeatingDegreeDaysScoreSubscription = this.heatingDegreeDaysScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchHeatingDegreeDaysScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationHeatingDegreeDaysChangeSubscription = this.heatingDegreeDaysChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchHeatingDegreeDaysChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationExtremeHotDaysSubscription = this.extremeHotDaysService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchExtremeHotDaysByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationExtremeHotDaysScoreSubscription = this.extremeHotDaysScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchExtremeHotDaysScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationExtremeHotDaysChangeSubscription = this.extremeHotDaysChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchExtremeHotDaysChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationVeryExtremeHotDaysSubscription = this.veryExtremeHotDaysService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchVeryExtremeHotDaysByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationVeryExtremeHotDaysScoreSubscription = this.veryExtremeHotDaysScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchVeryExtremeHotDaysScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationVeryExtremeHotDaysChangeSubscription = this.veryExtremeHotDaysChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchVeryExtremeHotDaysChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimatePrSubscription = this.climatePrService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimatePrScoreSubscription = this.climatePrScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimatePrChangeSubscription = this.climatePrChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateCddSubscription = this.climateCddService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateCddByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateCddScoreSubscription = this.climateCddScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateCddScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateCddChangeSubscription = this.climateCddChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateCddChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateRx1daySubscription = this.climateRx1dayService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx1dayByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateRx1dayScoreSubscription = this.climateRx1dayScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx1dayScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateRx1dayChangeSubscription = this.climateRx1dayChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx1dayChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateRx5daySubscription = this.climateRx5dayService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx5dayByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateRx5dayScoreSubscription = this.climateRx5dayScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx5dayScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateRx5dayChangeSubscription = this.climateRx5dayChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx5dayChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimatePrsnSubscription = this.climatePrsnService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrsnByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimatePrsnScoreSubscription = this.climatePrsnScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrsnScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimatePrsnChangeSubscription = this.climatePrsnChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrsnChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateSstSubscription = this.climateSstService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSstByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateSstScoreSubscription = this.climateSstScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSstScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateSstChangeSubscription = this.climateSstChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSstChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateSfcWindSubscription = this.climateSfcWindService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSfcWindByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateSfcWindScoreSubscription = this.climateSfcWindScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSfcWindScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateSfcWindChangeSubscription = this.climateSfcWindChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSfcWindChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateTrSubscription = this.climateTrService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTrByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateTrScoreSubscription = this.climateTrScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTrScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateTrChangeSubscription = this.climateTrChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTrChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateSlrSubscription = this.climateSlrService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSlrByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateSlrScoreSubscription = this.climateSlrScoreService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSlrScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.locationClimateSlrChangeSubscription = this.climateSlrChangeService.getLocationChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSlrChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });
  }

  subscribeAddressChartInstances(location: Point, datasetID: string) {
    this.addressProfileMaxTemperatureSubscription = this.maxTemperatureService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchMaxTemperatureByLocation(location, datasetID, instance);
    });

    this.addressProfileMeanTemperatureSubscription = this.meanTemperatureService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchMeanTemperatureByLocation(location, datasetID, instance);
    });

    this.addressProfileMinTemperatureSubscription = this.minTemperatureService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchMinTemperatureByLocation(location, datasetID, instance);
    });

    this.addressProfileSnowfallSubscription = this.snowfallService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSnowfallByLocation(location, datasetID, instance);
    });

    this.addressProfilePrecipitationSubscription = this.precipitationService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchPrecipitationByLocation(location, datasetID, instance);
    });

    this.addressProfileSurfaceWindSubscription = this.surfaceWindService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSurfacewindByLocation(location, datasetID, instance);
    });

    this.addressProfilePopulationDensitySubscription = this.populationDensityService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchPopulationDensityByLocation(location, datasetID, instance);
    });

    this.addressProfileSeaLevelRiseSubscription = this.seaLevelRiseService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSeaLevelRiseByLocation(location, datasetID, instance);
    });

    this.addressProfileSeaLevelRiseWarmingSubscription = this.seaLevelRiseWarmingService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSeaLevelRiseWarmingByLocation(location, datasetID, instance);
    });
  }

  subscribeAddressClimatologyChartInstances(location: Point, datasetID: string, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.addressProfileSpi6Subscription = this.spi6Service.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSpi6ByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileSpi6ScoreSubscription = this.spi6ScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSpi6ScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileSpi6ChangeSubscription = this.spi6ChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchSpi6ChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileCoolingDegreeDaysSubscription = this.coolingDegreeDaysService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchCoolingDegreeDaysByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileCoolingDegreeDaysScoreSubscription = this.coolingDegreeDaysScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchCoolingDegreeDaysScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileCoolingDegreeDaysChangeSubscription = this.coolingDegreeDaysChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchCoolingDegreeDaysChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileTxxSubscription = this.txxService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchTxxByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileTxxScoreSubscription = this.txxScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchTxxScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileTxxChangeSubscription = this.txxChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchTxxChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateTSubscription = this.climateTService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateTScoreSubscription = this.climateTScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateTChangeSubscription = this.climateTChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileFrostDaysSubscription = this.frostDaysService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchFrostDaysByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileFrostDaysScoreSubscription = this.frostDaysScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchFrostDaysScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileFrostDaysChangeSubscription = this.frostDaysChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchFrostDaysChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileHeatingDegreeDaysSubscription = this.heatingDegreeDaysService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchHeatingDegreeDaysByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileHeatingDegreeDaysScoreSubscription = this.heatingDegreeDaysScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchHeatingDegreeDaysScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileHeatingDegreeDaysChangeSubscription = this.heatingDegreeDaysChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchHeatingDegreeDaysChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileExtremeHotDaysSubscription = this.extremeHotDaysService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchExtremeHotDaysByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileExtremeHotDaysScoreSubscription = this.extremeHotDaysScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchExtremeHotDaysScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileExtremeHotDaysChangeSubscription = this.extremeHotDaysChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchExtremeHotDaysChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileVeryExtremeHotDaysSubscription = this.veryExtremeHotDaysService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchVeryExtremeHotDaysByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileVeryExtremeHotDaysScoreSubscription = this.veryExtremeHotDaysScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchVeryExtremeHotDaysScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileVeryExtremeHotDaysChangeSubscription = this.veryExtremeHotDaysChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchVeryExtremeHotDaysChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimatePrSubscription = this.climatePrService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimatePrScoreSubscription = this.climatePrScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimatePrChangeSubscription = this.climatePrChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateCddSubscription = this.climateCddService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateCddByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateCddScoreSubscription = this.climateCddScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateCddScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateCddChangeSubscription = this.climateCddChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateCddChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateRx1daySubscription = this.climateRx1dayService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx1dayByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateRx1dayScoreSubscription = this.climateRx1dayScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx1dayScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateRx1dayChangeSubscription = this.climateRx1dayChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx1dayChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateRx5daySubscription = this.climateRx5dayService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx5dayByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateRx5dayScoreSubscription = this.climateRx5dayScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx5dayScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateRx5dayChangeSubscription = this.climateRx5dayChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateRx5dayChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimatePrsnSubscription = this.climatePrsnService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrsnByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimatePrsnScoreSubscription = this.climatePrsnScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrsnScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimatePrsnChangeSubscription = this.climatePrsnChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimatePrsnChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateSstSubscription = this.climateSstService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSstByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateSstScoreSubscription = this.climateSstScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSstScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateSstChangeSubscription = this.climateSstChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSstChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateSfcWindSubscription = this.climateSfcWindService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSfcWindByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateSfcWindScoreSubscription = this.climateSfcWindScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSfcWindScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateSfcWindChangeSubscription = this.climateSfcWindChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSfcWindChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateTrSubscription = this.climateTrService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTrByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateTrScoreSubscription = this.climateTrScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTrScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateTrChangeSubscription = this.climateTrChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateTrChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateSlrSubscription = this.climateSlrService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSlrByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateSlrScoreSubscription = this.climateSlrScoreService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSlrScoreByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });

    this.addressProfileClimateSlrChangeSubscription = this.climateSlrChangeService.getAddressChartSubject().pipe(take(1)).subscribe(instance => {
      this.fetchClimateSlrChangeByLocation(location, datasetID, instance, climateOverlayPermission, changeDetector);
    });
  }

  fetchMaxTemperatureByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchMaxTemperatureByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      this.downloadRequestPayload.maxTemperature = climateChart;
      this.hideChartInstance(instance);
      this.maxTemperatureService.updateMaxTempChart(instance, climateChart);
    });
  }

  fetchMeanTemperatureByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchMeanTemperatureByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      this.downloadRequestPayload.meanTemperature = climateChart;
      this.hideChartInstance(instance);
      this.meanTemperatureService.updateMeanTempChart(instance, climateChart);
    });
  }

  fetchMinTemperatureByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchMinTemperatureByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      this.downloadRequestPayload.minTemperature = climateChart;
      this.hideChartInstance(instance);
      this.minTemperatureService.updateMinTempChart(instance, climateChart);
    });
  }

  fetchSnowfallByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchSnowfallByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      this.downloadRequestPayload.snowfallTemperature = climateChart;
      this.hideChartInstance(instance);
      this.snowfallService.updateSnowfallChart(instance, climateChart);
    });
  }

  fetchPrecipitationByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchPrecipitationByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      this.downloadRequestPayload.precipitation = climateChart;
      this.hideChartInstance(instance);
      this.precipitationService.updatePrecipitationChart(instance, climateChart);
    });
  }

  fetchSurfacewindByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchSurfacewindByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      this.downloadRequestPayload.surfaceWind = climateChart;
      this.hideChartInstance(instance);
      this.surfaceWindService.updateSurfaceWindChart(instance, climateChart);
    });
  }

  fetchPopulationDensityByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchPopulationDensityByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      this.downloadRequestPayload.populationDensity = climateChart;
      this.hideChartInstance(instance);
      this.populationDensityService.updatePopulationDensityChart(instance, climateChart);
    });
  }

  fetchSeaLevelRiseByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchSeaLevelRiseByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      this.downloadRequestPayload.seaLevelRise = climateChart;
      this.hideChartInstance(instance);
      this.seaLevelRiseService.updateSeaLevelRiseChart(instance, climateChart);
    });
  }

  fetchSeaLevelRiseWarmingByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchSeaLevelRiseWarmingByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      this.downloadRequestPayload.seaLevelRiseWarming = climateChart;
      this.hideChartInstance(instance);
      this.seaLevelRiseWarmingService.updateSeaLevelRiseWarmingChart(instance, climateChart);
    });
  }

  fetchSpi6ByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchSpi6ByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      // Validate if chart has actual data
      const hasValidData = this.isChartDataValid(climateChart);
      
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        // Disable the chart to hide the container if permission object is provided
        if (climateOverlayPermission) {
          climateOverlayPermission.isSpi6Enabled = false;
          // Trigger change detection to update the UI
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      
      // Only store and update chart if data is valid
      this.downloadClimatologyRequestPayload.spi6 = climateChart;
      this.spi6Service.updateSpi6Chart(instance, climateChart);
    });
  }

  fetchSpi6ScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchSpi6ScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      // Validate if chart has actual data
      const hasValidData = this.isChartDataValid(climateChart);
      
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        // Disable the chart to hide the container if permission object is provided
        if (climateOverlayPermission) {
          climateOverlayPermission.isSpi6ScoreEnabled = false;
          // Trigger change detection to update the UI
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      
      // Only store and update chart if data is valid
      this.downloadClimatologyRequestPayload.spi6Score = climateChart;
      this.spi6ScoreService.updateSpi6ScoreChart(instance, climateChart);
    });
  }

  fetchSpi6ChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchSpi6ChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      // Validate if chart has actual data
      const hasValidData = this.isChartDataValid(climateChart);
      
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        // Disable the chart to hide the container if permission object is provided
        if (climateOverlayPermission) {
          climateOverlayPermission.isSpi6ChangeEnabled = false;
          // Trigger change detection to update the UI
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      
      // Only store and update chart if data is valid
      this.downloadClimatologyRequestPayload.spi6Change = climateChart;
      this.spi6ChangeService.updateSpi6ChangeChart(instance, climateChart);
    });
  }

  fetchCoolingDegreeDaysByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchCoolingDegreeDaysByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isCoolingDegreeDaysEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      
      this.downloadClimatologyRequestPayload.coolingDegreeDays = climateChart;
      this.coolingDegreeDaysService.updateCoolingDegreeDaysChart(instance, climateChart);
    });
  }

  fetchCoolingDegreeDaysScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchCoolingDegreeDaysScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isCoolingDegreeDaysScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.coolingDegreeDaysScore = climateChart;
      this.coolingDegreeDaysScoreService.updateCoolingDegreeDaysScoreChart(instance, climateChart);
    });
  }

  fetchCoolingDegreeDaysChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchCoolingDegreeDaysChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isCoolingDegreeDaysChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.coolingDegreeDaysChange = climateChart;
      this.coolingDegreeDaysChangeService.updateCoolingDegreeDaysChangeChart(instance, climateChart);
    });
  }

  fetchTxxByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchTxxByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isTxxEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.txx = climateChart;
      this.txxService.updateTxxChart(instance, climateChart);
    });
  }

  fetchTxxScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchTxxScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isTxxScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.txxScore = climateChart;
      this.txxScoreService.updateTxxScoreChart(instance, climateChart);
    });
  }

  fetchTxxChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchTxxChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isTxxChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.txxChange = climateChart;
      this.txxChangeService.updateTxxChangeChart(instance, climateChart);
    });
  }

  fetchClimateTByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateTByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateTEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateT = climateChart;
      this.climateTService.updateClimateTChart(instance, climateChart);
    });
  }

  fetchClimateTScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateTScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateTScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateTScore = climateChart;
      this.climateTScoreService.updateClimateTScoreChart(instance, climateChart);
    });
  }

  fetchClimateTChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateTChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateTChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateTChange = climateChart;
      this.climateTChangeService.updateClimateTChangeChart(instance, climateChart);
    });
  }

  fetchFrostDaysByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchFrostDaysByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isFrostDaysEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.frostDays = climateChart;
      this.frostDaysService.updateFrostDaysChart(instance, climateChart);
    });
  }

  fetchFrostDaysScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchFrostDaysScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isFrostDaysScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.frostDaysScore = climateChart;
      this.frostDaysScoreService.updateFrostDaysScoreChart(instance, climateChart);
    });
  }

  fetchFrostDaysChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchFrostDaysChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isFrostDaysChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.frostDaysChange = climateChart;
      this.frostDaysChangeService.updateFrostDaysChangeChart(instance, climateChart);
    });
  }

  fetchHeatingDegreeDaysByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchHeatingDegreeDaysByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isHeatingDegreeDaysEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.heatingDegreeDays = climateChart;
      this.heatingDegreeDaysService.updateHeatingDegreeDaysChart(instance, climateChart);
    });
  }

  fetchHeatingDegreeDaysScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchHeatingDegreeDaysScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isHeatingDegreeDaysScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.heatingDegreeDaysScore = climateChart;
      this.heatingDegreeDaysScoreService.updateHeatingDegreeDaysScoreChart(instance, climateChart);
    });
  }

  fetchHeatingDegreeDaysChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchHeatingDegreeDaysChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isHeatingDegreeDaysChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.heatingDegreeDaysChange = climateChart;
      this.heatingDegreeDaysChangeService.updateHeatingDegreeDaysChangeChart(instance, climateChart);
    });
  }

  fetchExtremeHotDaysByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchExtremeHotDaysByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isExtremeHotDaysEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.extremeHotDays = climateChart;
      this.extremeHotDaysService.updateExtremeHotDaysChart(instance, climateChart);
    });
  }

  fetchExtremeHotDaysScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchExtremeHotDaysScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isExtremeHotDaysScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.extremeHotDaysScore = climateChart;
      this.extremeHotDaysScoreService.updateExtremeHotDaysScoreChart(instance, climateChart);
    });
  }

  fetchExtremeHotDaysChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchExtremeHotDaysChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isExtremeHotDaysChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.extremeHotDaysChange = climateChart;
      this.extremeHotDaysChangeService.updateExtremeHotDaysChangeChart(instance, climateChart);
    });
  }

  fetchVeryExtremeHotDaysByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchVeryExtremeHotDaysByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isVeryExtremeHotDaysEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.veryExtremeHotDays = climateChart;
      this.veryExtremeHotDaysService.updateVeryExtremeHotDaysChart(instance, climateChart);
    });
  }

  fetchVeryExtremeHotDaysScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchVeryExtremeHotDaysScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isVeryExtremeHotDaysScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.veryExtremeHotDaysScore = climateChart;
      this.veryExtremeHotDaysScoreService.updateVeryExtremeHotDaysScoreChart(instance, climateChart);
    });
  }

  fetchVeryExtremeHotDaysChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchVeryExtremeHotDaysChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isVeryExtremeHotDaysChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.veryExtremeHotDaysChange = climateChart;
      this.veryExtremeHotDaysChangeService.updateVeryExtremeHotDaysChangeChart(instance, climateChart);
    });
  }

  fetchClimatePrByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimatePrByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimatePrEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climatePr = climateChart;
      this.climatePrService.updateClimatePrChart(instance, climateChart);
    });
  }

  fetchClimatePrScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimatePrScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimatePrScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climatePrScore = climateChart;
      this.climatePrScoreService.updateClimatePrScoreChart(instance, climateChart);
    });
  }

  fetchClimatePrChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimatePrChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimatePrChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climatePrChange = climateChart;
      this.climatePrChangeService.updateClimatePrChangeChart(instance, climateChart);
    });
  }

  fetchClimateCddByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateCddByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateCddEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateCdd = climateChart;
      this.climateCddService.updateClimateCddChart(instance, climateChart);
    });
  }

  fetchClimateCddScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateCddScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateCddScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateCddScore = climateChart;
      this.climateCddScoreService.updateClimateCddScoreChart(instance, climateChart);
    });
  }

  fetchClimateCddChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateCddChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateCddChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateCddChange = climateChart;
      this.climateCddChangeService.updateClimateCddChangeChart(instance, climateChart);
    });
  }

  fetchClimateRx1dayByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateRx1dayByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateRx1dayEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateRx1day = climateChart;
      this.climateRx1dayService.updateClimateRx1dayChart(instance, climateChart);
    });
  }

  fetchClimateRx1dayScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateRx1dayScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateRx1dayScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateRx1dayScore = climateChart;
      this.climateRx1dayScoreService.updateClimateRx1dayScoreChart(instance, climateChart);
    });
  }

  fetchClimateRx1dayChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateRx1dayChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateRx1dayChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateRx1dayChange = climateChart;
      this.climateRx1dayChangeService.updateClimateRx1dayChangeChart(instance, climateChart);
    });
  }

  fetchClimateRx5dayByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateRx5dayByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateRx5dayEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateRx5day = climateChart;
      this.climateRx5dayService.updateClimateRx5dayChart(instance, climateChart);
    });
  }

  fetchClimateRx5dayScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateRx5dayScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateRx5dayScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateRx5dayScore = climateChart;
      this.climateRx5dayScoreService.updateClimateRx5dayScoreChart(instance, climateChart);
    });
  }

  fetchClimateRx5dayChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateRx5dayChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateRx5dayChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateRx5dayChange = climateChart;
      this.climateRx5dayChangeService.updateClimateRx5dayChangeChart(instance, climateChart);
    });
  }

  fetchClimatePrsnByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimatePrsnByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimatePrsnEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climatePrsn = climateChart;
      this.climatePrsnService.updateClimatePrsnChart(instance, climateChart);
    });
  }

  fetchClimatePrsnScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimatePrsnScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimatePrsnScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climatePrsnScore = climateChart;
      this.climatePrsnScoreService.updateClimatePrsnScoreChart(instance, climateChart);
    });
  }

  fetchClimatePrsnChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimatePrsnChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimatePrsnChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climatePrsnChange = climateChart;
      this.climatePrsnChangeService.updateClimatePrsnChangeChart(instance, climateChart);
    });
  }

  fetchClimateSstByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateSstByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateSstEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateSst = climateChart;
      this.climateSstService.updateClimateSstChart(instance, climateChart);
    });
  }

  fetchClimateSstScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateSstScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateSstScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateSstScore = climateChart;
      this.climateSstScoreService.updateClimateSstScoreChart(instance, climateChart);
    });
  }

  fetchClimateSstChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateSstChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateSstChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateSstChange = climateChart;
      this.climateSstChangeService.updateClimateSstChangeChart(instance, climateChart);
    });
  }

  fetchClimateSfcWindByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateSfcWindByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateSfcWindEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateSfcWind = climateChart;
      this.climateSfcWindService.updateClimateSfcWindChart(instance, climateChart);
    });
  }

  fetchClimateSfcWindScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateSfcWindScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateSfcWindScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateSfcWindScore = climateChart;
      this.climateSfcWindScoreService.updateClimateSfcWindScoreChart(instance, climateChart);
    });
  }

  fetchClimateSfcWindChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateSfcWindChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateSfcWindChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateSfcWindChange = climateChart;
      this.climateSfcWindChangeService.updateClimateSfcWindChangeChart(instance, climateChart);
    });
  }

  fetchClimateTrByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateTrByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateTrEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateTr = climateChart;
      this.climateTrService.updateClimateTrChart(instance, climateChart);
    });
  }

  fetchClimateTrScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateTrScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateTrScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateTrScore = climateChart;
      this.climateTrScoreService.updateClimateTrScoreChart(instance, climateChart);
    });
  }

  fetchClimateTrChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateTrChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateTrChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateTrChange = climateChart;
      this.climateTrChangeService.updateClimateTrChangeChart(instance, climateChart);
    });
  }

  fetchClimateSlrByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateSlrByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateSlrEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateSlr = climateChart;
      this.climateSlrService.updateClimateSlrChart(instance, climateChart);
    });
  }

  fetchClimateSlrScoreByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateSlrScoreByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateSlrScoreEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateSlrScore = climateChart;
      this.climateSlrScoreService.updateClimateSlrScoreChart(instance, climateChart);
    });
  }

  fetchClimateSlrChangeByLocation(location: Point, datasetID: string, instance: any, climateOverlayPermission?: ClimateOverlayPermission, changeDetector?: any) {
    this.climateService.fetchClimateSlrChangeByLocation(location, datasetID).subscribe((climateChart: ClimateChart) => {
      const hasValidData = this.isChartDataValid(climateChart);
      this.hideChartInstance(instance);
      
      if (!hasValidData) {
        if (climateOverlayPermission) {
          climateOverlayPermission.isClimateSlrChangeEnabled = false;
          if (changeDetector) {
            changeDetector.markForCheck();
          }
        }
        return;
      }
      this.downloadClimatologyRequestPayload.climateSlrChange = climateChart;
      this.climateSlrChangeService.updateClimateSlrChangeChart(instance, climateChart);
    });
  }

  hideChartInstance(instance) {
    if (instance !== undefined) {
      instance.hideLoading();
    }
  }

  unsubscribeAddressChartInstances() {
    this.addressProfileMaxTemperatureSubscription?.unsubscribe();
    this.addressProfileMeanTemperatureSubscription?.unsubscribe();
    this.addressProfileMinTemperatureSubscription?.unsubscribe();
    this.addressProfileSnowfallSubscription?.unsubscribe();
    this.addressProfilePrecipitationSubscription?.unsubscribe();
    this.addressProfileSurfaceWindSubscription?.unsubscribe();
    this.addressProfilePopulationDensitySubscription?.unsubscribe();
    this.addressProfileSeaLevelRiseSubscription?.unsubscribe();
    this.addressProfileSeaLevelRiseWarmingSubscription?.unsubscribe();
    this.addressProfileSpi6Subscription?.unsubscribe();
    this.addressProfileSpi6ScoreSubscription?.unsubscribe();
    this.addressProfileSpi6ChangeSubscription?.unsubscribe();
    this.addressProfileCoolingDegreeDaysSubscription?.unsubscribe();
    this.addressProfileCoolingDegreeDaysScoreSubscription?.unsubscribe();
    this.addressProfileCoolingDegreeDaysChangeSubscription?.unsubscribe();
    this.addressProfileTxxSubscription?.unsubscribe();
    this.addressProfileTxxScoreSubscription?.unsubscribe();
    this.addressProfileTxxChangeSubscription?.unsubscribe();
    this.addressProfileClimateTSubscription?.unsubscribe();
    this.addressProfileClimateTScoreSubscription?.unsubscribe();
    this.addressProfileClimateTChangeSubscription?.unsubscribe();
    this.addressProfileFrostDaysSubscription?.unsubscribe();
    this.addressProfileFrostDaysScoreSubscription?.unsubscribe();
    this.addressProfileFrostDaysChangeSubscription?.unsubscribe();
    this.addressProfileHeatingDegreeDaysSubscription?.unsubscribe();
    this.addressProfileHeatingDegreeDaysScoreSubscription?.unsubscribe();
    this.addressProfileHeatingDegreeDaysChangeSubscription?.unsubscribe();
    this.addressProfileExtremeHotDaysSubscription?.unsubscribe();
    this.addressProfileExtremeHotDaysScoreSubscription?.unsubscribe();
    this.addressProfileExtremeHotDaysChangeSubscription?.unsubscribe();
    this.addressProfileVeryExtremeHotDaysSubscription?.unsubscribe();
    this.addressProfileVeryExtremeHotDaysScoreSubscription?.unsubscribe();
    this.addressProfileVeryExtremeHotDaysChangeSubscription?.unsubscribe();
    this.addressProfileClimatePrSubscription?.unsubscribe();
    this.addressProfileClimatePrScoreSubscription?.unsubscribe();
    this.addressProfileClimatePrChangeSubscription?.unsubscribe();
    this.addressProfileClimateCddSubscription?.unsubscribe();
    this.addressProfileClimateCddScoreSubscription?.unsubscribe();
    this.addressProfileClimateCddChangeSubscription?.unsubscribe();
    this.addressProfileClimateRx1daySubscription?.unsubscribe();
    this.addressProfileClimateRx1dayScoreSubscription?.unsubscribe();
    this.addressProfileClimateRx1dayChangeSubscription?.unsubscribe();
    this.addressProfileClimateRx5daySubscription?.unsubscribe();
    this.addressProfileClimateRx5dayScoreSubscription?.unsubscribe();
    this.addressProfileClimateRx5dayChangeSubscription?.unsubscribe();
    this.addressProfileClimatePrsnSubscription?.unsubscribe();
    this.addressProfileClimatePrsnScoreSubscription?.unsubscribe();
    this.addressProfileClimatePrsnChangeSubscription?.unsubscribe();
    this.addressProfileClimateSstSubscription?.unsubscribe();
    this.addressProfileClimateSstScoreSubscription?.unsubscribe();
    this.addressProfileClimateSstChangeSubscription?.unsubscribe();
    this.addressProfileClimateSfcWindSubscription?.unsubscribe();
    this.addressProfileClimateSfcWindScoreSubscription?.unsubscribe();
    this.addressProfileClimateSfcWindChangeSubscription?.unsubscribe();
    this.addressProfileClimateTrSubscription?.unsubscribe();
    this.addressProfileClimateTrScoreSubscription?.unsubscribe();
    this.addressProfileClimateTrChangeSubscription?.unsubscribe();
    this.addressProfileClimateSlrSubscription?.unsubscribe();
    this.addressProfileClimateSlrScoreSubscription?.unsubscribe();
    this.addressProfileClimateSlrChangeSubscription?.unsubscribe();
  }

  unsubscribeLocationChartInstances() {
    this.locationMaxTemperatureSubscription?.unsubscribe();
    this.locationMeanTemperatureSubscription?.unsubscribe();
    this.locationMinTemperatureSubscription?.unsubscribe();
    this.locationSnowfallSubscription?.unsubscribe();
    this.locationPrecipitationSubscription?.unsubscribe();
    this.locationSurfaceWindSubscription?.unsubscribe();
    this.locationPopulationDensitySubscription?.unsubscribe();
    this.locationSeaLevelRiseSubscription?.unsubscribe();
    this.locationSeaLevelRiseWarmingSubscription?.unsubscribe();
    this.locationSpi6Subscription?.unsubscribe();
    this.locationSpi6ScoreSubscription?.unsubscribe();
    this.locationSpi6ChangeSubscription?.unsubscribe();
    this.locationCoolingDegreeDaysSubscription?.unsubscribe();
    this.locationCoolingDegreeDaysScoreSubscription?.unsubscribe();
    this.locationCoolingDegreeDaysChangeSubscription?.unsubscribe();
    this.locationTxxSubscription?.unsubscribe();
    this.locationTxxScoreSubscription?.unsubscribe();
    this.locationTxxChangeSubscription?.unsubscribe();
    this.locationClimateTSubscription?.unsubscribe();
    this.locationClimateTScoreSubscription?.unsubscribe();
    this.locationClimateTChangeSubscription?.unsubscribe();
    this.locationFrostDaysSubscription?.unsubscribe();
    this.locationFrostDaysScoreSubscription?.unsubscribe();
    this.locationFrostDaysChangeSubscription?.unsubscribe();
    this.locationHeatingDegreeDaysSubscription?.unsubscribe();
    this.locationHeatingDegreeDaysScoreSubscription?.unsubscribe();
    this.locationHeatingDegreeDaysChangeSubscription?.unsubscribe();
    this.locationExtremeHotDaysSubscription?.unsubscribe();
    this.locationExtremeHotDaysScoreSubscription?.unsubscribe();
    this.locationExtremeHotDaysChangeSubscription?.unsubscribe();
    this.locationVeryExtremeHotDaysSubscription?.unsubscribe();
    this.locationVeryExtremeHotDaysScoreSubscription?.unsubscribe();
    this.locationVeryExtremeHotDaysChangeSubscription?.unsubscribe();
    this.locationClimatePrSubscription?.unsubscribe();
    this.locationClimatePrScoreSubscription?.unsubscribe();
    this.locationClimatePrChangeSubscription?.unsubscribe();
    this.locationClimateCddSubscription?.unsubscribe();
    this.locationClimateCddScoreSubscription?.unsubscribe();
    this.locationClimateCddChangeSubscription?.unsubscribe();
    this.locationClimateRx1daySubscription?.unsubscribe();
    this.locationClimateRx1dayScoreSubscription?.unsubscribe();
    this.locationClimateRx1dayChangeSubscription?.unsubscribe();
    this.locationClimateRx5daySubscription?.unsubscribe();
    this.locationClimateRx5dayScoreSubscription?.unsubscribe();
    this.locationClimateRx5dayChangeSubscription?.unsubscribe();
    this.locationClimatePrsnSubscription?.unsubscribe();
    this.locationClimatePrsnScoreSubscription?.unsubscribe();
    this.locationClimatePrsnChangeSubscription?.unsubscribe();
    this.locationClimateSstSubscription?.unsubscribe();
    this.locationClimateSstScoreSubscription?.unsubscribe();
    this.locationClimateSstChangeSubscription?.unsubscribe();
    this.locationClimateSfcWindSubscription?.unsubscribe();
    this.locationClimateSfcWindScoreSubscription?.unsubscribe();
    this.locationClimateSfcWindChangeSubscription?.unsubscribe();
    this.locationClimateTrSubscription?.unsubscribe();
    this.locationClimateTrScoreSubscription?.unsubscribe();
    this.locationClimateTrChangeSubscription?.unsubscribe();
    this.locationClimateSlrSubscription?.unsubscribe();
    this.locationClimateSlrScoreSubscription?.unsubscribe();
    this.locationClimateSlrChangeSubscription?.unsubscribe();
  }

  resetDownloadPayload() {
    this.downloadRequestPayload = {
      maxTemperature: null,
      meanTemperature: null,
      minTemperature: null,
      snowfallTemperature: null,
      precipitation: null,
      surfaceWind: null,
      populationDensity: null,
      seaLevelRise: null,
      seaLevelRiseWarming: null
    };
  }

  resetDownloadClimatologyPayload() {
    this.downloadClimatologyRequestPayload = {
      spi6: null,
      spi6Score: null,
      spi6Change: null,
      coolingDegreeDays: null,
      coolingDegreeDaysScore: null,
      coolingDegreeDaysChange: null,
      txx: null,
      txxScore: null,
      txxChange: null,
      climateT: null,
      climateTScore: null,
      climateTChange: null,
      frostDays: null,
      frostDaysScore: null,
      frostDaysChange: null,
      heatingDegreeDays: null,
      heatingDegreeDaysScore: null,
      heatingDegreeDaysChange: null,
      extremeHotDays: null,
      extremeHotDaysScore: null,
      extremeHotDaysChange: null,
      veryExtremeHotDays: null,
      veryExtremeHotDaysScore: null,
      veryExtremeHotDaysChange: null,
      climatePr: null,
      climatePrScore: null,
      climatePrChange: null,
      climateCdd: null,
      climateCddScore: null,
      climateCddChange: null,
      climateRx1day: null,
      climateRx1dayScore: null,
      climateRx1dayChange: null,
      climateRx5day: null,
      climateRx5dayScore: null,
      climateRx5dayChange: null,
      climatePrsn: null,
      climatePrsnScore: null,
      climatePrsnChange: null,
      climateSst: null,
      climateSstScore: null,
      climateSstChange: null,
      climateSfcWind: null,
      climateSfcWindScore: null,
      climateSfcWindChange: null,
      climateTr: null,
      climateTrScore: null,
      climateTrChange: null,
      climateSlr: null,
      climateSlrScore: null,
      climateSlrChange: null
    };
  }
}
